import * as React from 'react';

import { enumUnit } from '@rematter/frontend-lib-api-client';
import {
  Accordion,
  cn,
  humanReadableShortPricingUnit,
  MultipleAccordion,
} from '@rematter/ui-web';
import { useAtomValue } from 'jotai';

import {
  $inventoryAvailabilityFacilityData,
  $isInventoryAvailabilityLoading,
  $itemAvailabilities,
} from '../../_state/inventoryAvailability.state';
import { useTranslations } from '../../i18n/useTranslations';

const ContentWrapper: React.FC<
  React.PropsWithChildren<{ className?: string }>
> = ({ children, className }) => (
  <div
    className={cn(
      '@ContentWrapper',
      'min-w-56 border-l border-neutral/10 bg-neutral/5 p-4',
      className,
    )}
  >
    {children}
  </div>
);

export const InventoryAvailability: React.FC = () => {
  const t = useTranslations();

  const isLoading = useAtomValue($isInventoryAvailabilityLoading);
  const itemAvailabilities = useAtomValue($itemAvailabilities);
  const facility = useAtomValue($inventoryAvailabilityFacilityData);

  if (itemAvailabilities.length === 0) {
    return null;
  }

  if (isLoading) {
    return (
      <ContentWrapper>
        <div className="text-sm text-foreground/50">
          {t.itemTable.inventoryAvailability.loadingData}
        </div>
      </ContentWrapper>
    );
  }

  return (
    <ContentWrapper className="@InventoryAvailability">
      <div className="text-xs font-medium text-neutral/60 uppercase">
        {t.itemTable.inventoryAvailability.title}
      </div>
      <div className="mb-2 text-sm font-semibold">{facility?.facilityName}</div>
      <MultipleAccordion variant="ghost">
        {itemAvailabilities.map((item) => (
          <Accordion
            key={item.itemId}
            size="sm"
            variant="ghost"
            trigger={item.itemName}
          >
            <div className="grid text-sm">
              <div className="flex justify-between font-medium">
                <span>{t.itemTable.inventoryAvailability.loose}</span>
                <span>
                  {item.looseWeight.toLocaleString()}{' '}
                  <span className="font-normal text-neutral/60">
                    {humanReadableShortPricingUnit(enumUnit.Pounds)}
                  </span>
                </span>
              </div>
              <div className="flex justify-between font-medium">
                <span>{t.itemTable.inventoryAvailability.tagged}</span>
                <span>
                  {item.taggedWeight.toLocaleString()}{' '}
                  <span className="font-normal text-neutral/60">
                    {humanReadableShortPricingUnit(enumUnit.Pounds)}
                  </span>
                </span>
              </div>
            </div>
          </Accordion>
        ))}
      </MultipleAccordion>
    </ContentWrapper>
  );
};
